/**
 * @license Copyright (c) 2003-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import { ClassicEditor as ClassicEditorBase } from "@ckeditor/ckeditor5-editor-classic";

import { Alignment } from "@ckeditor/ckeditor5-alignment";
import { Autosave } from "@ckeditor/ckeditor5-autosave";
import {
  Bold,
  Italic,
  Subscript,
  Superscript,
  Underline,
} from "@ckeditor/ckeditor5-basic-styles";
import { Clipboard } from "@ckeditor/ckeditor5-clipboard";
import { Comments } from "@ckeditor/ckeditor5-comments";
import { Essentials } from "@ckeditor/ckeditor5-essentials";
import { Font } from "@ckeditor/ckeditor5-font";
import { Heading } from "@ckeditor/ckeditor5-heading";
import { HorizontalLine } from "@ckeditor/ckeditor5-horizontal-line";
import { GeneralHtmlSupport } from "@ckeditor/ckeditor5-html-support";
import { Image } from "@ckeditor/ckeditor5-image";
import { Indent } from "@ckeditor/ckeditor5-indent";
import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
import { SourceEditing } from "@ckeditor/ckeditor5-source-editing";
import { SpecialCharacters } from "@ckeditor/ckeditor5-special-characters";
import { Style } from "@ckeditor/ckeditor5-style";
import {
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
} from "@ckeditor/ckeditor5-table";
import { TextTransformation } from "@ckeditor/ckeditor5-typing";
import {
  TrackChanges,
  TrackChangesPreview,
} from "@ckeditor/ckeditor5-track-changes";
import { Undo } from "@ckeditor/ckeditor5-undo";
import MathType from "@wiris/mathtype-ckeditor5";
//import { WProofreader } from "./plugins/webspellchecker/wproofreader-ckeditor5/src";
//import { WProofreader } from "@webspellchecker/wproofreader-ckeditor5";

import ApplyTemplate from "./plugins/ApplyTemplate";
import BlockStyle from "./plugins/BlockStyle";
import CBTPBT from "./plugins/CBTPBT";
import DeleteReferenceContent from "./plugins/DeleteReferenceContent";
import Interaction from "./plugins/Interaction";
import LineRefs from "./plugins/LineRefs";
import ListProperties from "./plugins/ckeditor5-list/src/listproperties";
import Marks from "./plugins/Marks";
import MediaInsert from "./plugins/MediaInsert";
import NoBreak from "./plugins/NoBreak";
import PassageUnderline from "./plugins/PassageUnderline";
import PreviewImageInsert from "./plugins/PreviewImageInsert";
import ShowBlocks from "./plugins/ShowBlocks";
import ShowCharacters from "./plugins/ShowCharacters";
import SpecialCharactersContent from "./plugins/SpecialCharacters";
import SymbolShortcuts from "./plugins/SymbolShortcuts";
import { DEFAULT_SYMBOLS } from "./plugins/SymbolShortcuts/symbols";
import TrackChangesIntegration from "./plugins/TrackChangesIntegration";
import CustomTableCellProperties from "./plugins/CustomTabelCellProperties";

export default class CDEClassicEditor extends ClassicEditorBase {
  // Toolbar item names contributed by the SymbolShortcuts plugin (CDE-16014),
  // derived from the plugin's own symbol definitions. Consumers that build
  // their own toolbar (see client/src/components/Editor) read the names from
  // here instead of duplicating them, so adding a symbol to DEFAULT_SYMBOLS is
  // enough to surface a new button.
  public static symbolShortcutItems = DEFAULT_SYMBOLS.map(({ name }) => name);

  // Plugins to include in the build.
  public static override builtinPlugins = [
    Alignment,
    ApplyTemplate,
    Autosave,
    BlockStyle,
    Bold,
    CBTPBT,
    Clipboard,
    Comments,
    // CustomTableCellProperties,
    DeleteReferenceContent,
    Essentials,
    Font,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    Image,
    Indent,
    Interaction,
    Italic,
    LineRefs,
    ListProperties,
    Marks,
    MathType,
    MediaInsert,
    NoBreak,
    Paragraph,
    PassageUnderline,
    PreviewImageInsert,
    ShowBlocks,
    ShowCharacters,
    SourceEditing,
    SpecialCharacters,
    SpecialCharactersContent,
    Style,
    Subscript,
    Superscript,
    SymbolShortcuts,
    Table,
    TableCellProperties,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TrackChanges,
    TrackChangesIntegration,
    // Adds "Preview final content" to the track changes dropdown (CDE+):
    // shows the content with all suggestions accepted in a read-only dialog
    // without applying them to the document.
    TrackChangesPreview,
    Underline,
    Undo,
    //WProofreader,
  ];

  // Editor configuration.
  public static override defaultConfig = {
    toolbar: {
      items: [
        "fontSize",
        "|",
        "bold",
        "italic",
        "underline",
        "subscript",
        "superscript",
        "horizontalLine",
        "passageUnderline",
        "|",
        "alignment:left",
        "alignment:center",
        "alignment:right",
        "alignment:justify",
        "|",
        "bulletedList",
        "numberedList",
        "|",
        "insertTable",
        "|",
        "specialCharacters",
        "|",
        "MathType",
        "ChemType",
        //"wproofreader",
        "|",
        "sourceEditing",
        "undo",
        "redo",
      ],
    },
    style: {
      definitions: [
        {
          classes: ["passage-underline"],
          element: "span",
          name: "Passage Underline",
        },
      ],
    },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableProperties",
        // "customTableCellProperties",
        "tableCellProperties",
      ],
      tableProperties: {
        defaultProperties: {
          borderCollapse: "collapse",
          borderStyle: "solid",
          borderWidth: "1px",
          borderSpacing: "0px",
        },
      },
      tableCellProperties: {
        defaultProperties: {
          padding: "3px",
          borderStyle: "solid",
          borderWidth: "1px",
        },
      },
    },
    // This value must be kept in sync with the language defined in webpack.config.js.
    language: "en",
  };
}
